import { getQueuedVoiceCalls, IQueueCallback } from "@Api/abstract/queued_voice_call.api.abstract";
import { useAsync } from "@Hooks";
import { useFeatureFlag } from "@Hooks/useFeatureFlag";
import { useEffect } from "react";

const noop = () => {};

const useQueuedVoiceCalls = (query: string) => {
  const isFeatureEnabled = useFeatureFlag("journey.call.back", false);

  const {
    data: queuedVoiceCalls = [],
    error,
    loading,
    refetch = noop,
    reset = noop,
  } = useAsync(() => getQueuedVoiceCalls(query), "QUEUED_VOICE_CALLS_" + query, {
    skip: !isFeatureEnabled,
  });

  useEffect(() => {
    if (!isFeatureEnabled) return;
    refetch(); // trigger initial fetch right away
  }, [query, isFeatureEnabled]);

  if (!isFeatureEnabled) {
    return {
      queueCallbacks: [],
      error: null,
      loading: false,
      fetchQueuedVoiceCalls: noop,
    };
  }

  return {
    queuedVoiceCalls: error ? [] : queuedVoiceCalls,
    error,
    loading,
    fetchQueuedVoiceCalls: refetch,
  };
};

const useQueuedVoiceCall = (voiceCallId: number) => {
  const query = encodeURIComponent(
    JSON.stringify({
      voiceCall: { id: { $eq: voiceCallId } },
    })
  );
  const { queuedVoiceCalls, error, loading, fetchQueuedVoiceCalls } = useQueuedVoiceCalls(query);

  const queuedVoiceCall: IQueueCallback | null =
    queuedVoiceCalls && queuedVoiceCalls.length > 0 ? queuedVoiceCalls[0] : null;

  return {
    queuedVoiceCall,
    error,
    loading,
    fetchQueuedVoiceCalls,
  };
};

const useQueuedVoiceCallFromUserId = (userId: number) => {
  const query = encodeURIComponent(
    JSON.stringify({
      user: { id: { $eq: userId } },
      voiceCall: { endTime: { $eq: null } },
    })
  );

  const { queuedVoiceCalls, error, loading, fetchQueuedVoiceCalls } = useQueuedVoiceCalls(query);

  const queuedVoiceCall: IQueueCallback | null =
    queuedVoiceCalls && queuedVoiceCalls.length > 0 ? queuedVoiceCalls[0] : null;

  return {
    queuedVoiceCall,
    error,
    loading,
    fetchQueuedVoiceCalls,
  };
};

const useQueuedVoiceCallFromConversationId = (conversationId: number) => {
  const query = encodeURIComponent(
    JSON.stringify({
      conversation: { id: { $eq: conversationId } },
    })
  );
  const { queuedVoiceCalls, error, loading, fetchQueuedVoiceCalls } = useQueuedVoiceCalls(query);

  return {
    queuedVoiceCalls,
    error,
    loading,
    fetchQueuedVoiceCalls,
  };
};

export {
  useQueuedVoiceCalls,
  useQueuedVoiceCall,
  useQueuedVoiceCallFromUserId,
  useQueuedVoiceCallFromConversationId,
};
